/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { submissionApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ExamSubmission } from '../types';

const MySubmissions: React.FC = () => {
    const [submissions, setSubmissions] = useState<ExamSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                if (user?.id) {
                    const data = await submissionApi.getByStudent(user.id);
                    setSubmissions(data);
                }
            } catch (error) {
                console.error('Error fetching submissions:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSubmissions();
    }, [user?.id]);

    const getScoreEmoji = (score: number, total: number) => {
        const percentage = (score / total) * 100;
        if (percentage >= 90) return '🏆';
        if (percentage >= 75) return '🌟';
        if (percentage >= 50) return '👍';
        return '💪';
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div data-testid="my-submissions">
            <h1 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📊 My Exam Results
            </h1>

            {submissions.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-emoji">📭</div>
                    <h3 className="empty-state-title">No Submissions Yet</h3>
                    <p>Take an exam to see your results here!</p>
                    <Link to="/" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                        📚 View Available Exams
                    </Link>
                </div>
            ) : (
                <div className="grid grid-2">
                    {submissions.map((sub) => (
                        <Link
                            key={sub.id}
                            to={`/result/${sub.id}`}
                            className="card"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                <div style={{ fontSize: '3rem' }}>
                                    {getScoreEmoji(sub.totalScore || 0, sub.totalMarks || 1)}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 className="card-title" style={{ marginBottom: '0.5rem' }}>
                                        {sub.examTitle}
                                        {sub.attemptNumber && sub.attemptNumber > 1 && (
                                            <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', color: '#718096' }}>
                                                ({(() => { const n=sub.attemptNumber!; const s=["th","st","nd","rd"],v=n%100; return n + (s[(v-20)%10]||s[v]||s[0]); })()} Attempt)
                                            </span>
                                        )}
                                    </h3>
                                    <div className="exam-info" style={{ marginBottom: '0.5rem' }}>
                                        <div className="exam-info-item">
                                            <span className="icon">📊</span>
                                            <span>
                                                Score: {sub.totalScore} / {sub.totalMarks}
                                            </span>
                                        </div>
                                        <div className="exam-info-item">
                                            <span className="icon">📅</span>
                                            <span>
                                                {sub.submittedAt
                                                    ? new Date(sub.submittedAt).toLocaleDateString()
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    {sub.isEvaluated ? (
                                        <span className="badge badge-success">✅ Fully Evaluated</span>
                                    ) : (
                                        <span className="badge badge-warning">⏳ Pending Evaluation</span>
                                    )}
                                </div>
                            </div>
                            <div className="progress-bar" style={{ marginTop: '1rem' }}>
                                <div
                                    className="progress-fill"
                                    style={{
                                        width: `${((sub.totalScore || 0) / (sub.totalMarks || 1)) * 100}%`,
                                    }}
                            ></div>
                            </div>
                            </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

export default MySubmissions;
